import React, { useEffect, useState } from "react";
import { AddCircleOutline, Autorenew, Edit, Search } from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useHistory } from "react-router-dom";

import { ALL, INITIAL_PAGING, PAGINATION_LIMIT } from "../../constants";
import dayjs from "dayjs";
import { COLOR, TRANSPARENT_COLOR } from "../../styles/color";
import NoData from "../../components/NoData";
import { handleCallApiError } from "../../errors";
import DatePickerRange from "../../components/PickDateRange";
import {
  BOOKING_STATUS,
  BOOKING_STATUS_LABEL,
  BOOKING_TYPE,
  BOOKING_TYPE_LABEL,
} from "../../constants/booking";
import apis from "../../apis";
import { StyledTable } from "../../components/Table/index.style";
import CustomTableFooter from "../../components/Table/TableFooter";
import debounce from "../../utils/debounce";
import BookingNoteDialog from "./BookingNoteDialog";
import HistoryDialog from "../CustomerDetail/HistoryDialog";
import OrderDialog from "../../components/OrderDialog";
import { INIT_ORDER } from "../../constants/order";

const INIT_DATE = [
  dayjs().subtract(1, "month").startOf("month"),
  dayjs().add(1, "month").endOf("month"),
];

const INIT_HISTORY = {
  userId: null,
  serviceDuration: 30,
  status: "",
  feedback: "",
  note: "",
};

const Row = ({
  row,
  index,
  customerId,
  setItem,
  setType,
  setNoteDialogOpen,
  setHistoryDialog,
  setOrderItem,
  setHistory,
  setOrder,
  setCustomer,
  setOrderDialog,
}) => {
  const history = useHistory();
  const [open, setOpen] = React.useState(false);

  const renderOrderStatus = (status) => {
    switch (status) {
      case BOOKING_STATUS.COMPLETED:
        return (
          <Typography sx={{ color: COLOR.success }} fontSize="13px">
            {BOOKING_STATUS_LABEL[status]}
          </Typography>
        );

      case BOOKING_STATUS.PENDING:
        return (
          <Typography sx={{ color: COLOR.error }} fontSize="13px">
            {BOOKING_STATUS_LABEL[status]}
          </Typography>
        );

      case BOOKING_STATUS.CANCELLED:
        return (
          <Typography sx={{ color: COLOR.dark }} fontSize="13px">
            {BOOKING_STATUS_LABEL[status]}
          </Typography>
        );

      default:
        return <>status</>;
    }
  };

  return (
    <React.Fragment>
      <TableRow
        hover
        sx={{
          "& > *": { borderBottom: "unset" },
          cursor: "pointer",
        }}
        className="body-row"
        onClick={() => setOpen(!open)}
      >
        <TableCell className="body-cell-collapse" align="center">
          {index + 1}
        </TableCell>
        {!customerId && (
          <>
            <TableCell
              className="body-cell-collapse"
              component="th"
              scope="row"
            >
              <Typography
                sx={{ cursor: "pointer" }}
                color="primary"
                fontSize="13px"
                onClick={(e) => {
                  e.stopPropagation();
                  history.push(`/customer/${row.customer.id}`);
                }}
              >
                {row.customer.name}
              </Typography>
            </TableCell>
            <TableCell className="body-cell-collapse" align="left">
              {row.customer.phoneNumber}
            </TableCell>
          </>
        )}
        <TableCell className="body-cell-collapse" align="left">
          {`${row.bookingTime} ${dayjs(row.bookingDate, "YYYY-MM-DD").format(
            "DD/MM/YYYY"
          )}`}
        </TableCell>
        <TableCell className="body-cell-collapse" align="left">
          {row.note}
        </TableCell>
        <TableCell className="body-cell-collapse" align="left">
          {[row.friendName, row.friendPhoneNumber].filter(Boolean).join(" - ")}
        </TableCell>
        <TableCell className="body-cell-collapse" align="left">
          {renderOrderStatus(row.status)}
        </TableCell>
        <TableCell className="body-cell-collapse">
          <Stack direction="row" justifyContent="center" alignItems="center">
            <Tooltip title="Cập nhật đặt lịch">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setItem(row);
                  setType("booking");
                  setNoteDialogOpen(true);
                }}
              >
                <Edit sx={{ color: COLOR.purple }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          style={{
            paddingBottom: 0,
            paddingTop: 0,
            backgroundColor: TRANSPARENT_COLOR.light,
          }}
          colSpan={9}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            {row.bookingItems.length ? (
              <Box my={1}>
                <Typography fontWeight={500}>Chi tiết</Typography>
                <Table size="small" aria-label="purchases">
                  <TableHead>
                    <TableRow>
                      <TableCell align="left">STT</TableCell>
                      <TableCell align="left">Dịch vụ</TableCell>
                      <TableCell align="left">Loại</TableCell>
                      <TableCell align="left">Thời lượng</TableCell>
                      <TableCell align="left">Buổi</TableCell>
                      <TableCell align="left">Ghi chú</TableCell>
                      <TableCell align="center">...</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {row.bookingItems.map((item, index) => (
                      <TableRow hover key={index}>
                        <TableCell align="left">{index + 1}</TableCell>
                        <TableCell align="left">{item.name}</TableCell>
                        <TableCell align="left">
                          {BOOKING_TYPE_LABEL[item.type]}
                        </TableCell>
                        <TableCell align="left">{item.duration} phút</TableCell>
                        <TableCell align="left">
                          {item.type === BOOKING_TYPE.COURSE
                            ? `${item.session} / ${item.orderItem.numberOfSessions}`
                            : "1/1"}
                        </TableCell>
                        <TableCell align="left">{item.note}</TableCell>
                        <TableCell>
                          <Stack
                            direction="row"
                            justifyContent="center"
                            alignItems="center"
                          >
                            <Tooltip title="Thêm ghi chú">
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setItem(item);
                                  setType("booking-item");
                                  setNoteDialogOpen(true);
                                }}
                              >
                                <Edit sx={{ color: COLOR.purple }} />
                              </IconButton>
                            </Tooltip>
                            {item.type === BOOKING_TYPE.COURSE && (
                              <Tooltip title="Thêm buổi sử dụng">
                                <IconButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOrderItem(item.orderItem);
                                    setHistory((prev) => ({
                                      ...prev,
                                      serviceDuration: item.duration,
                                    }));
                                    setHistoryDialog(true);
                                  }}
                                  disabled={
                                    row.status !== BOOKING_STATUS.PENDING ||
                                    item.orderItem.usageHistories.length >=
                                      item.session
                                  }
                                >
                                  <AddCircleOutline
                                    color={
                                      row.status !== BOOKING_STATUS.PENDING ||
                                      item.orderItem.usageHistories.length >=
                                        item.session
                                        ? ""
                                        : "error"
                                    }
                                  />
                                </IconButton>
                              </Tooltip>
                            )}
                            {item.type === BOOKING_TYPE.SINGLE && (
                              <Tooltip title="Đăng ký dịch vụ">
                                <IconButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOrder((prev) => ({
                                      ...prev,
                                      orderItems: [
                                        {
                                          serviceId: item.service.id,
                                          numberOfSessions: 1,
                                          discount: item.service.discount,
                                          discountPercent: 0,
                                        },
                                      ],
                                    }));
                                    setCustomer(row.customer);
                                    setOrderDialog(true);
                                  }}
                                  disabled={
                                    row.status !== BOOKING_STATUS.PENDING
                                  }
                                >
                                  <AddCircleOutline
                                    color={
                                      row.status !== BOOKING_STATUS.PENDING
                                        ? ""
                                        : "error"
                                    }
                                  />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ) : (
              <NoData />
            )}
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

const Customer = ({ customerId = undefined }) => {
  const [search, setSearch] = useState("");
  const [paging, setPaging] = useState(INITIAL_PAGING);
  const [date, setDate] = useState(INIT_DATE);
  const [status, setStatus] = useState(ALL);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [item, setItem] = useState(null);
  const [type, setType] = useState("");
  const [history, setHistory] = useState(INIT_HISTORY);
  const [historyDialog, setHistoryDialog] = useState(false);
  const [orderItem, setOrderItem] = useState({});
  const [order, setOrder] = useState(INIT_ORDER);
  const [orderDialog, setOrderDialog] = useState(false);
  const [customer, setCustomer] = useState({});

  const handleChangePage = (page) => {
    setPaging((prev) => ({ ...prev, page }));
  };

  const handleChangeSearch = (event) => {
    const { value } = event.target;
    setSearch(value);
    handleChangePage(INITIAL_PAGING.page);
  };

  const handleChangeStatus = (event) => {
    const { value } = event.target;
    setStatus(value);
    handleChangePage(INITIAL_PAGING.page);
  };

  const handleChangeDate = (newValue) => {
    setDate(newValue);
    handleChangePage(INITIAL_PAGING.page);
  };

  const handleRefresh = () => {
    setDate(INIT_DATE);
    setPaging(INITIAL_PAGING);
  };

  const handleCloseNoteDialog = () => {
    setNoteDialogOpen(false);
    setItem(null);
    setType("");
  };

  const handleCloseHistoryDialog = () => {
    setHistoryDialog(false);
    setOrderItem({});
    setHistory(INIT_HISTORY);
  };

  const handleCloseOrderDialog = () => {
    setOrderDialog(false);
    setCustomer({});
    setOrder(INIT_ORDER);
  };

  const fetchBookings = async (displayLoading = false) => {
    if (displayLoading) setLoading(true);
    try {
      const [startDate, endDate] = date;
      const condition = {
        search,
        status: status !== ALL ? status : null,
        startDate: dayjs(startDate).format("YYYY-MM-DD"),
        endDate: dayjs(endDate).format("YYYY-MM-DD"),
        customerId,
        limit: PAGINATION_LIMIT,
        offset: (paging.page - 1) * PAGINATION_LIMIT,
      };
      const res = await apis.booking.getBookings(condition);
      setBookings(res.bookings);
      setPaging((prev) => ({ ...prev, total: res.total || 0 }));
    } catch (error) {
      handleCallApiError(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    debounce(() => fetchBookings(true), 500)(search);
  }, [paging.page, status, date, search]);

  return (
    <>
      <Box padding={2}>
        <Grid container spacing={2} marginBottom={2}>
          {!customer && (
            <Grid item xs={6} sm={6} md={4} lg={4}>
              <TextField
                value={search}
                size="small"
                fullWidth
                placeholder={"Tìm kiếm theo tên hoặc số điện thoại"}
                onChange={handleChangeSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="end">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                minwidth="400px"
              />
            </Grid>
          )}
          <Grid item xs={6} sm={6} md={2} lg={2}>
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">Trạng thái</InputLabel>
              <Select
                labelId="status-select-label"
                value={status}
                onChange={handleChangeStatus}
                size="small"
                label="Trạng thái"
              >
                <MenuItem value={ALL}>Tất cả</MenuItem>
                {Object.keys(BOOKING_STATUS_LABEL).map((key) => (
                  <MenuItem key={key} value={key}>
                    {BOOKING_STATUS_LABEL[key]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <Box display="flex">
              <DatePickerRange value={date} onChange={handleChangeDate} />
              <Tooltip title="Đặt lại">
                <IconButton aria-label="refresh" onClick={handleRefresh}>
                  <Autorenew />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <Stack
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
            ></Stack>
          </Grid>
        </Grid>
        <StyledTable component={Box}>
          <Table aria-label="collapsible table">
            <TableHead>
              <TableRow>
                <TableCell className="header-cell" align="center">
                  <Typography className="header-title">STT</Typography>
                </TableCell>
                {!customerId && (
                  <>
                    <TableCell className="header-cell" align="left">
                      <Typography className="header-title">
                        Khách hàng
                      </Typography>
                    </TableCell>
                    <TableCell className="header-cell" align="left">
                      <Typography className="header-title">SĐT</Typography>
                    </TableCell>
                  </>
                )}
                <TableCell className="header-cell" align="left">
                  <Typography className="header-title">Thời gian</Typography>
                </TableCell>
                <TableCell className="header-cell" align="left">
                  <Typography className="header-title">Ghi chú</Typography>
                </TableCell>
                <TableCell className="header-cell" align="left">
                  <Typography className="header-title">Bạn đi cùng</Typography>
                </TableCell>
                <TableCell className="header-cell" align="left">
                  <Typography className="header-title">Trạng thái</Typography>
                </TableCell>
                <TableCell className="header-cell" align="center">
                  <Typography className="header-title">...</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow className="body-row">
                  <TableCell colSpan={7}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : (
                bookings.map((row, index) => (
                  <Row
                    key={index}
                    customerId={customerId}
                    row={row}
                    index={index}
                    setItem={setItem}
                    setType={setType}
                    setNoteDialogOpen={setNoteDialogOpen}
                    setHistoryDialog={setHistoryDialog}
                    setOrderItem={setOrderItem}
                    setHistory={setHistory}
                    setCustomer={setCustomer}
                    setOrder={setOrder}
                    setOrderDialog={setOrderDialog}
                  />
                ))
              )}
            </TableBody>
          </Table>
          {bookings && bookings.length ? (
            <CustomTableFooter
              total={paging.total}
              page={paging.page}
              onChangePage={handleChangePage}
            />
          ) : (
            !loading && <NoData />
          )}
        </StyledTable>
      </Box>
      <BookingNoteDialog
        open={noteDialogOpen}
        handleClose={handleCloseNoteDialog}
        type={type}
        item={item}
        reload={fetchBookings}
      />
      <HistoryDialog
        open={historyDialog}
        handleClose={handleCloseHistoryDialog}
        orderItem={orderItem}
        history={history}
        setHistory={setHistory}
        reload={() => {}}
        dialogType="create"
      />
      <OrderDialog
        open={orderDialog}
        handleClose={handleCloseOrderDialog}
        customer={customer}
        order={order}
        setOrder={setOrder}
      />
    </>
  );
};

export default Customer;
